import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Clock, CheckCircle, XCircle, Eye, FileText } from 'lucide-react';
import ExpenseModal from '../components/ExpenseModal';
import { formatINR } from '../utils/currency';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtCur = (amount, currency) => {
  const val = Number(amount || 0);
  if (currency === 'USD') return `$${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (currency === 'EUR') return `€${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (currency === 'GBP') return `£${val.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return formatINR(val);
};

const MyRequests = () => {
  const { user } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => { fetchExpenses(); }, [user]);

  const fetchExpenses = async () => {
    try {
      const response = await axios.get(`${API}/expenses`, { params: { employee_id: user.id } });
      setExpenses(response.data);
    } catch (error) {
      console.error('Error fetching expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    await axios.delete(`${API}/expenses/${expenseId}`);
    setSelectedExpense(null);
    fetchExpenses();
  };

  const getStatusBadge = (expense) => {
    const s = expense.approval_status;
    const t = expense.settlement_status;
    const step = expense.current_approval_step;
    if (s === 'pending' && step && step !== 'done') {
      const labels = { dgm: 'DGM', management: 'Mgmt', hr: 'HR', accounts: 'Accounts' };
      return <span className="status-badge status-pending"><Clock className="w-3 h-3 mr-1" />@ {labels[step] || step}</span>;
    }
    if (s === 'pending') return <span className="status-badge status-pending"><Clock className="w-3 h-3 mr-1" />Pending</span>;
    if (s === 'rejected') return <span className="status-badge status-rejected"><XCircle className="w-3 h-3 mr-1" />Rejected</span>;
    if (t === 'reimbursed') return <span className="status-badge status-paid"><CheckCircle className="w-3 h-3 mr-1" />Reimbursed</span>;
    if (t === 'company_paid') return <span className="status-badge status-approved"><CheckCircle className="w-3 h-3 mr-1" />Company Paid</span>;
    return <span className="status-badge status-approved"><CheckCircle className="w-3 h-3 mr-1" />Approved</span>;
  };

  const filteredExpenses = filter === 'all' ? expenses : expenses.filter(exp => {
    if (filter === 'pending') return exp.approval_status === 'pending';
    if (filter === 'approved') return exp.approval_status === 'approved' && exp.settlement_status !== 'reimbursed';
    if (filter === 'rejected') return exp.approval_status === 'rejected';
    if (filter === 'paid') return exp.settlement_status === 'reimbursed';
    return true;
  });

  if (loading) {
    return <div className="flex items-center justify-center h-screen"><div className="text-gray-500">Loading...</div></div>;
  }

  const FilterButton = ({ value, label, count }) => (
    <button onClick={() => setFilter(value)}
      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
        filter === value ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      }`}
      data-testid={`filter-${value}`}
    >
      {label} ({count})
    </button>
  );

  return (
    <div className="p-6 md:p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>My Requests</h1>
        <p className="text-sm text-gray-500 mt-1">Track the status of your expense submissions</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        <FilterButton value="all" label="All" count={expenses.length} />
        <FilterButton value="pending" label="Pending" count={expenses.filter(e => e.approval_status === 'pending').length} />
        <FilterButton value="approved" label="Approved" count={expenses.filter(e => e.approval_status === 'approved' && e.settlement_status !== 'reimbursed').length} />
        <FilterButton value="paid" label="Reimbursed" count={expenses.filter(e => e.settlement_status === 'reimbursed').length} />
        <FilterButton value="rejected" label="Rejected" count={expenses.filter(e => e.approval_status === 'rejected').length} />
      </div>

      <div className="card">
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Brand / Division</th>
                <th>Category</th>
                <th>Vendor</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredExpenses.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-8">
                    <FileText className="w-10 h-10 text-gray-200 mx-auto mb-2" />
                    <p className="text-gray-400 text-sm">No expenses found</p>
                  </td>
                </tr>
              ) : (
                filteredExpenses.map((expense) => (
                  <tr key={expense.id} data-testid={`expense-row-${expense.id}`}>
                    <td className="whitespace-nowrap">{new Date(expense.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</td>
                    <td>
                      <span className="text-xs font-medium px-2 py-0.5 rounded bg-slate-100 text-slate-600">
                        {expense.brand_division || 'N/A'}
                      </span>
                    </td>
                    <td>{expense.category}</td>
                    <td>{expense.vendor}</td>
                    <td className="font-semibold">{fmtCur(expense.amount, expense.currency || 'INR')}</td>
                    <td>{getStatusBadge(expense)}</td>
                    <td>
                      <button onClick={() => setSelectedExpense(expense)}
                        className="text-indigo-600 hover:text-indigo-800 font-medium text-xs flex items-center gap-1"
                        data-testid={`view-expense-${expense.id}`}
                      >
                        <Eye className="w-3.5 h-3.5" />View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedExpense && (
        <ExpenseModal
          expense={selectedExpense}
          onClose={() => setSelectedExpense(null)}
          onDelete={user?.role === 'admin' ? handleDeleteExpense : undefined}
          showActions={false}
        />
      )}
    </div>
  );
};

export default MyRequests;
